import { Alert, AlertTitle, Collapse, Stack, Typography } from "@mui/material";
import { groupByToMap } from "@entrilia/shared/utilities/arrayHelper";
import { formatNumber } from "@entrilia/shared/utilities/formatters";
import { pluralize, separatedString } from "@entrilia/shared/utilities/stringHelper";
import type { ObjectDuplicate } from "../../api/types/objectTypes";
import ObjectPageLink from "./ObjectPageLink";
import type { ObjectDuplicates } from "./useObjectDuplicates";

const duplicatesShownWhenCollapsed = 3;

const formatSummaryValue = (value: unknown): string | undefined => {
  if (typeof value === "number") {
    return formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return typeof value === "string" ? value : undefined;
};

const getDuplicateSummary = ({ summaryValues }: ObjectDuplicate): string =>
  separatedString(" • ")(...summaryValues.map(formatSummaryValue)) || "Untitled";

interface Props {
  objectType: string;
  objectDuplicates: ObjectDuplicates;
}

const ObjectDuplicatesCheck = ({ objectType, objectDuplicates: { response, hasFailed } }: Props) => {
  const duplicates = response?.duplicates ?? [];

  if (!hasFailed && duplicates.length === 0) {
    return null;
  }

  const detailsHidden = response?.detailsHidden;
  const shownDuplicates =
    !detailsHidden && duplicates.length - duplicatesShownWhenCollapsed > 1
      ? duplicates.slice(0, duplicatesShownWhenCollapsed)
      : duplicates;
  const hiddenCount = duplicates.length - shownDuplicates.length;
  const duplicatesByMatch = [...groupByToMap(shownDuplicates, (duplicate) => duplicate.matchDescription)];

  return (
    <Collapse appear in sx={{ width: "100%" }}>
      {hasFailed ? (
        <Alert severity="info" sx={{ textAlign: "left" }}>
          We couldn&apos;t check for duplicates.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ width: "100%", textAlign: "left" }}>
          <AlertTitle>
            {duplicates.length} possible {pluralize("duplicate", duplicates.length)}
          </AlertTitle>
          <Stack spacing={1}>
            {duplicatesByMatch.map(([matchDescription, matchedDuplicates]) =>
              detailsHidden ? (
                <Typography key={matchDescription} variant="inherit">
                  {matchDescription} ({matchedDuplicates.length})
                </Typography>
              ) : (
                <Stack key={matchDescription}>
                  <Typography variant="inherit">{matchDescription}:</Typography>
                  {matchedDuplicates.map((duplicate) =>
                    duplicate.objectId ? (
                      <ObjectPageLink
                        key={duplicate.objectId}
                        objectId={duplicate.objectId}
                        objectType={objectType}
                        color="inherit"
                        underline="always"
                      >
                        {getDuplicateSummary(duplicate)}
                      </ObjectPageLink>
                    ) : null
                  )}
                </Stack>
              )
            )}
            {hiddenCount > 0 && <Typography variant="inherit">+{hiddenCount} more</Typography>}
          </Stack>
        </Alert>
      )}
    </Collapse>
  );
};

export default ObjectDuplicatesCheck;
